/**
 * useIsReadOnlyAllView — Plus tier "All Portfolios" view guard.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026).
 *
 * Returns `true` when the user is on the combined All Portfolios
 * view AND they have at least one named portfolio (i.e. they're on
 * Plus tier with multiple portfolios). In that mode the All view is
 * a read-only summary — buy/sell, cash transactions, goal-setting,
 * delete, and DRIP actions are blocked because the All view is a
 * roll-up, not a real bucket to act inside.
 *
 *   Free tier            → false (legacy single-bucket behaviour)
 *   Plus, on All view    → true  (read-only summary)
 *   Plus, on a portfolio → false (normal — every action allowed)
 *
 * Components consume this to hide buttons and surface a banner
 * pointing users back to the PortfolioSelector dropdown if they
 * want to act.
 */
import { useSyncExternalStore } from 'react';
import { getSnapshot, subscribe } from './portfolioStore';

function _snap() {
  const s = getSnapshot();
  // Round P108y (Feb 2026) — the guard now fires for ANY Plus user
  // on the All view, including brand-new Plus users with zero
  // named portfolios. Previously the gate required `hasGroups > 0`,
  // which let first-time Plus users silently drop positions into
  // an unassigned pool on their very first buy. Now they're always
  // routed through the "name a portfolio first" flow, matching the
  // CSV Import guard (Round P108f).
  const isAllView = !s.activeId;
  const hasGroups = (s.portfolios?.length || 0) > 0;
  const isPlus = !!s.isPlus;
  return {
    isReadOnly: isAllView && isPlus,
    isAllView,
    hasGroups,
    isPlus,
  };
}

let _cache = _snap();
let _cacheKey = `${_cache.isReadOnly}|${_cache.isAllView}|${_cache.hasGroups}|${_cache.isPlus}`;

function _stable() {
  const next = _snap();
  const key = `${next.isReadOnly}|${next.isAllView}|${next.hasGroups}|${next.isPlus}`;
  if (key !== _cacheKey) {
    _cacheKey = key;
    _cache = next;
  }
  return _cache;
}

export function useIsReadOnlyAllView() {
  return useSyncExternalStore(subscribe, _stable, _stable);
}
